// the plain form reads a quoted value whole, so a `;` inside the quotes stays part of the name
const DISPOSITION_EXTENDED = /filename\*\s*=\s*(?:[\w-]+'[^']*')?([^;]+)/i;
const DISPOSITION_PLAIN = /filename\s*=\s*(?:"([^"]*)"|([^;]+))/i;

// some backends percent-encode the name and some interpolate it raw, so a literal `%` reaches this
// and would otherwise throw URIError and take the whole download down with it
function decodeSafely(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function parseDisposition(header: string | null | undefined): string | undefined {
  if (!header) return undefined;
  // RFC 6266: when a header carries both, the extended form wins
  const extended = DISPOSITION_EXTENDED.exec(header)?.[1];
  const plainMatch = DISPOSITION_PLAIN.exec(header);
  const plain = plainMatch?.[1] ?? plainMatch?.[2];
  const raw = (extended ?? plain)?.trim().replace(/^"|"$/gu, '');
  return raw ? decodeSafely(raw) : undefined;
}

// our cloud-storage gateway puts the real name in the `identifier` token's `name` claim
function parseGatewayIdentifier(token: string | null): string | undefined {
  const payload = token?.split('.')[1];
  if (!payload) return undefined;
  try {
    // atob yields latin-1 code units, so reading the string straight saves `résumé.pdf` as
    // `rÃ©sumÃ©.pdf` — decode the bytes
    const bytes = Uint8Array.from(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
      (character) => character.charCodeAt(0),
    );
    const name = (JSON.parse(new TextDecoder().decode(bytes)) as { name?: unknown }).name;
    return typeof name === 'string' && name ? name : undefined;
  } catch {
    return undefined;
  }
}

// a storage key can be an opaque id ("1", a uuid); saving a file under that is worse than letting
// the caller decide, so only a segment that looks like a file counts
export function getFileNameFromUrl(url: string): string | undefined {
  try {
    const lastSegment = new URL(url).pathname.split('/').pop();
    if (!lastSegment) return undefined;
    const decoded = decodeSafely(lastSegment);
    return decoded.includes('.') ? decoded : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The name the server declared, or nothing — in which case the caller's own filename wins.
 *
 * `Content-Disposition` is the source of truth. When a download ends on a storage host we cannot
 * read it, so the name is recovered from the final url instead: S3 repeats it in a query param,
 * our gateway hides it in the `identifier` token.
 *
 * Those two only stand in for a header we were not allowed to read, which is why they are gated on
 * the response being cross-origin. Same-origin the header was readable, so its absence is real and
 * the caller's own name has to win — that is what the anchor's `download` attribute did before we
 * fetched these bytes ourselves, and a disposition param on a url that never redirected must not
 * quietly outrank it.
 */
function isCrossOrigin(url: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URL(url, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
}

export function getServerDeclaredFileName(response: Response): string | undefined {
  const fromHeader = parseDisposition(response.headers.get('content-disposition'));
  if (fromHeader) return fromHeader;
  if (!isCrossOrigin(response.url)) return undefined;

  try {
    const params = new URL(response.url).searchParams;
    const fromPresign = parseDisposition(params.get('response-content-disposition'));
    if (fromPresign) return fromPresign;

    return parseGatewayIdentifier(params.get('identifier'));
  } catch {
    return undefined;
  }
}
